import { useEffect, useState } from 'react';
import { RefreshControl } from 'react-native';

import { Project } from '@graphql/generated';
import {
  logError,
  LogGraphError,
  LogNetworkError,
} from '@utils/logNonFatalError';

import type { ApolloError } from '@apollo/client';

export const DEFAULT_PER_PAGE_COUNT = 25;

export type ListProjectsBasicInfoSharedParams = {
  archivesOnly?: boolean;
  first?: number;
  onCompleted?: () => void;
  onError?: () => void;
};

// Minimal structural type satisfied by both ListProjectsBasicInfoQuery
// and ListProjectsBasicInfoForEditTaskQuery data shapes.
type ListProjectsSharedData = {
  listProjects?: {
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      endCursor?: string | null;
      startCursor?: string | null;
    };
    edges?: Array<{ node?: object | null } | null> | null;
  } | null;
};

export type ListProjectsSharedQueryResult = {
  data: ListProjectsSharedData | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => unknown;
  fetchMore: (options: {
    variables: { after?: string | null; first?: number };
  }) => unknown;
};

export const useListProjectsBasicInfoShared = (
  { data, loading, error, refetch, fetchMore }: ListProjectsSharedQueryResult,
  {
    archivesOnly = false,
    first = DEFAULT_PER_PAGE_COUNT,
    onCompleted,
    onError,
  }: ListProjectsBasicInfoSharedParams
) => {
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!loading) setRefreshing(false);
  }, [loading]);

  useEffect(() => {
    if (data) onCompleted?.();
  }, [data]);

  useEffect(() => {
    if (!error) return;
    if (error.networkError) {
      const event: LogNetworkError = {
        message: 'List Projects Network Failure',
        errorMessage: error.message,
        statusCode:
          'statusCode' in error.networkError
            ? error.networkError.statusCode
            : -1,
        extra: [{ archivesOnly }],
      };
      logError(event);
    }
    error.graphQLErrors.forEach((graphError) => {
      const event: LogGraphError = {
        message: 'List Projects GraphQL Error',
        errorMessage: graphError.message,
        operationName: 'listProjects',
        extra: [{ archivesOnly }],
      };
      logError(event);
    });
    onError?.();
  }, [error]);

  const refreshControl = (
    <RefreshControl
      refreshing={refreshing}
      onRefresh={() => {
        setRefreshing(true);
        refetch();
      }}
    />
  );

  const { listProjects } = data ?? { listProjects: undefined };
  const { edges = [], pageInfo } = listProjects ?? { edges: [] };
  const projects =
    edges?.reduce((acc, curr) => {
      if (curr?.node !== undefined) {
        return [...acc, curr.node as unknown as Project];
      } else {
        return acc;
      }
    }, [] as Project[]) ?? [];

  const fetchFromCursor = () => {
    if (!projects || !pageInfo?.hasNextPage || loading) return;

    const cursor = pageInfo.endCursor;
    fetchMore({
      variables: {
        after: cursor,
        first,
      },
    });
  };

  return {
    projects,
    loading,
    error,
    pageInfo,
    refetch,
    fetchFromCursor,
    refreshing,
    setRefreshing,
    refreshControl,
  };
};
